'use client';

import { iot, mqtt5 } from 'aws-iot-device-sdk-v2';
import { useEffect, useState } from 'react';

import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { once } from 'events';
import { toUtf8 } from '@aws-sdk/util-utf8-browser';

class AWSCognitoCredentialsProvider {
  options;
  cachedCredentials;
  constructor(options, expireIntervalInMs = 3600 * 1000) {
    this.options = options;
    this.cachedCredentials = null;

    setInterval(async () => {
      await this.refreshCredentials();
    }, expireIntervalInMs);
  }

  getCredentials() {
    return {
      aws_access_id: this.cachedCredentials?.accessKeyId ?? '',
      aws_secret_key: this.cachedCredentials?.secretAccessKey ?? '',
      aws_sts_token: this.cachedCredentials?.sessionToken,
      aws_region: this.options.Region,
    };
  }

  async refreshCredentials() {
    this.cachedCredentials = await fromCognitoIdentityPool({
      identityPoolId: this.options.IdentityPoolId,
      clientConfig: { region: this.options.Region },
    })();
  }
}

function UseMqttSocket({ awsRegion, awsCognitoIdentityPoolId, awsIotEndpoint, topicName = '' }) {
  const [client, setClient] = useState(null);
  const [mqttStatus, setMqttStatus] = useState({
    status: '',
    data: null,
  });

  useEffect(() => {
    async function onSuccessfulConnection() {
      const provider = new AWSCognitoCredentialsProvider({
        IdentityPoolId: awsCognitoIdentityPoolId,
        Region: awsRegion,
      });

      await provider.refreshCredentials();

      const mqttClient = createClient(provider);
      setClient(mqttClient);

      const attemptingConnect = once(mqttClient, 'attemptingConnect');
      const connectionSuccess = once(mqttClient, 'connectionSuccess');

      mqttClient.start();

      await attemptingConnect;
      await connectionSuccess;
    }
    onSuccessfulConnection();
    return () => {
      CloseConnection();
    };
  }, []);

  useEffect(() => {
    if (client && topicName) {
      SubscribeTopic(topicName);
    }
  }, [client, topicName]);

  function createClient(provider) {
    const wsConfig = {
      credentialsProvider: provider,
      region: awsRegion,
    };

    const builder = iot.AwsIotMqtt5ClientConfigBuilder.newWebsocketMqttBuilderWithSigv4Auth(awsIotEndpoint, wsConfig);

    const client = new mqtt5.Mqtt5Client(builder.build());

    client.on('error', (error) => {
      setMqttStatus({
        status: 'error',
        data: error,
      });
    });

    client.on('messageReceived', (eventData) => {
      setMqttStatus({
        status: 'messageReceived',
        data: eventData,
      });
      if (eventData?.message?.payload) {
        setMqttStatus({
          status: 'messageReceivedDeCode',
          data: JSON.parse(toUtf8(eventData.message.payload)),
        });
      }
    });

    client.on('attemptingConnect', () => {
      setMqttStatus({
        status: 'attemptingConnect',
      });
    });

    client.on('connectionSuccess', (eventData) => {
      setMqttStatus({
        status: 'connectionSuccess',
        data: eventData,
      });
    });

    client.on('connectionFailure', (eventData) => {
      setMqttStatus({
        status: 'connectionFailure',
        data: eventData,
      });
    });

    client.on('disconnection', (eventData) => {
      setMqttStatus({
        status: 'disconnection',
        data: eventData,
      });
      if (eventData.disconnect !== undefined) {
        setMqttStatus({
          status: 'disconnectionSuccess',
          data: eventData,
        });
      }
    });

    client.on('stopped', () => {
      setMqttStatus({
        status: 'stopped',
      });
    });
    return client;
  }

  const PublishMessage = async ({ message = '', topicPath = '' }) => {
    if (!client && !message && !topicPath) {
      console.log('send log error');
      return;
    }
    try {
      await client.publish({
        qos: mqtt5.QoS.AtLeastOnce,
        topicName: topicPath,
        payload: JSON.stringify(message),
      });
    } catch (error) {
      console.log(`Error publishing:`, error);
    }
  };

  const onConnection = async () => {
    if (!client) return;
    const attemptingConnect = once(client, 'attemptingConnect');
    const connectionSuccess = once(client, 'connectionSuccess');

    client.start();

    await attemptingConnect;
    await connectionSuccess;
  };

  const CloseConnection = async () => {
    if (!client) return;
    const disconnection = once(client, 'disconnection');
    const stopped = once(client, 'stopped');
    client.stop();
    await disconnection;
    await stopped;
  };

  const UnsubscribeTopic = async (topicPath = '') => {
    if (!client || !topicPath) return;
    await client.unsubscribe({
      topicFilters: [topicPath],
    });
  };

  const SubscribeTopic = async (SubTopicName = '') => {
    if (!client || !SubTopicName) return;
    await client.subscribe({
      subscriptions: [{ qos: mqtt5.QoS.AtLeastOnce, topicFilter: SubTopicName }],
    });
  };

  return {
    client,
    mqtt5,
    mqttStatus,
    SubscribeTopic,
    UnsubscribeTopic,
    onConnection,
    CloseConnection,
    PublishMessage,
  };
}

export default UseMqttSocket;
